import { RemirrorJSON } from "remirror";
import {
  IConcurrencyToken,
  IEntryRecord,
  ICreatedRecordParameters,
  IGenericRequestParameters,
  IModelWithIntId,
  IModificationRecord,
  IModifiedRecordParameters,
  INamedEntity,
  IPaginationParameters,
  IPermissionedEntity,
  IProjectBasedResponseModel,
  IVersionedEntity,
  IVersionedEntityParameters,
  ISoftDeletable,
  ISoftDeletableFilterParameters,
  EntityConstants,
  GenericEntityOrderBy,
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  IEntityMinimalModel,
  IProjectBasedParameters,
  ISessionedEntity,
  IUniqueEntity,
  IUniqueEntityParameters,
  ILockableEntity,
  ILockableEntityParameters,
  NamedEntityOrderBy,
} from "../../api/GenericTypes";
import {
  IGenericRequestParametersTranslator,
  PropertyTranslator,
  IPaginationParametersTranslator,
  IEntryRecordParametersTranslator,
  IModifiedRecordParametersTranslator,
  IVersionedEntityParametersTranslator,
  ISoftDeletableFilterParametersTranslator,
  IProjectBasedParametersTranslator,
  IEntryRecordLabels,
  IModificationRecordLabels,
  ISessionedEntityLabels,
  ISoftDeletableLabels,
  IConcurrencyTokenLabels,
  IPermissionedEntityLabels,
  INamedEntityLabels,
  IUniqueEntityLabels,
  IUniqueEntityParametersTranslator,
  ILockableEntityLabels,
  ILockableEntityParametersTranslator,
} from "../../api/GenericTranslator";

export interface LabNotebookTemplate
  extends IModelWithIntId,
    INamedEntity,
    IEntryRecord,
    IModificationRecord,
    IPermissionedEntity,
    IProjectBasedResponseModel,
    ISoftDeletable,
    IVersionedEntity,
    IConcurrencyToken<string>,
    ISessionedEntity,
    IUniqueEntity,
    ILockableEntity {
  description: string;
  content?: RemirrorJSON;
}
export interface LabNotebookTemplateSuggestions extends IEntityMinimalModel<LabNotebookTemplate["id"]> {}

export interface LabNotebookTemplateVersion extends LabNotebookTemplate, IVersionedEntity {}

export interface LabNotebookTemplateWriteModel
  extends Omit<
      LabNotebookTemplate,
      "id" | "enteredBy" | "enteredOn" | "modifiedBy" | "modifiedOn" | "isDeleted" | "version" | "originalId"
    >,
    IConcurrencyToken<string> {}

export interface LabNotebookTemplateFilters
  extends IGenericRequestParameters<LabNotebookTemplate, LabNotebookTemplateSortingOptions>,
    IPaginationParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    ISoftDeletableFilterParameters,
    IVersionedEntityParameters,
    IProjectBasedParameters,
    IUniqueEntityParameters,
    ILockableEntityParameters {}

export interface LabNotebookTemplateVersionFilter extends LabNotebookTemplateFilters, IVersionedEntityParameters {}

export const labNotebookTemplatesConstants: EntityConstants<LabNotebookTemplate, LabNotebookTemplateFilters> = {
  resource: "lab_notebook_templates",
  frontendIndexRoute: "lab_notebook_templates",
  entitySingular: "notebook template",
  entityPlural: "notebook templates",
  icon: "copy",
};

export const labNotebookTemplateFieldLabels: PropertyTranslator<LabNotebookTemplate> = {
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...ISessionedEntityLabels,
  ...ISoftDeletableLabels,
  ...IConcurrencyTokenLabels,
  ...IPermissionedEntityLabels,
  ...INamedEntityLabels,
  ...IUniqueEntityLabels,
  ...ILockableEntityLabels,
  content: "Content",
  id: "Notebook template ID",
  version: "Version",
  projects: "Projects",
  description: "Description",
};

export const LabNotebookTemplateFiltersTranslator: PropertyTranslator<LabNotebookTemplateFilters> = {
  ...IGenericRequestParametersTranslator,
  ...IPaginationParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...IProjectBasedParametersTranslator,
  ...IVersionedEntityParametersTranslator,
  ...ISoftDeletableFilterParametersTranslator,
  ...IUniqueEntityParametersTranslator,
  ...ILockableEntityParametersTranslator,
} as const;

export const LabNotebookTemplateSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
] as const;
export type LabNotebookTemplateSortingOptions = (typeof LabNotebookTemplateSortingOptionsConsts)[number];
